import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';

interface LogEntry {
    timestamp: string;
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
    data?: any;
}

interface LogContextProps {
    logs: LogEntry[];
    log: (level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: any) => void;
    clearLogs: () => void;
}

export const LogContext = createContext<LogContextProps | undefined>(undefined);

// Set to TRUE for Expo Go testing (uses Test Store: $rc_* products)
// Set to FALSE for production builds (uses real products: com.br4.tarot.credits.*)
const USE_TEST_STORE = true;

export const LogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const logsRef = useRef<LogEntry[]>([]);

    const log = (level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: any) => {
        const now = new Date();
        const timestamp = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) + '.' + now.getMilliseconds().toString().padStart(3, '0');
        const logEntry: LogEntry = { timestamp, level, message, data };

        // Always do console output
        const consoleMsg = `[${timestamp}] ${message}`;
        switch (level) {
            case 'error':
                console.error(consoleMsg, data || '');
                break;
            case 'warn':
                console.warn(consoleMsg, data || '');
                break;
            case 'debug':
            case 'info':
            default:
                console.log(consoleMsg, data || '');
                break;
        }

        // Only append to logs array in dev mode
        if (USE_TEST_STORE) {
            const newLogs = [...logsRef.current, logEntry];
            // Keep only last 100 logs to prevent memory issues
            const trimmedLogs = newLogs.slice(-100);
            logsRef.current = trimmedLogs;
            setLogs(trimmedLogs);
        }
    };

    const clearLogs = () => {
        logsRef.current = [];
        setLogs([]);
    };

    return (
        <LogContext.Provider value={{ logs, log, clearLogs }}>
            {children}
        </LogContext.Provider>
    );
};

export const useLog = (): LogContextProps => {
    const context = useContext(LogContext);
    if (!context) {
        throw new Error('useLog must be used within a LogProvider');
    }
    return context;
};
